import React from 'react';
import styled, { css } from 'styled-components';
import { header6 } from '@westwing/ui-kit/typography';
import { CrossIcon, Direction } from '@westwing/ui-kit/Icons/StyledIcons';
import { forIE } from '@westwing/ui-kit/breakPoints';
import { Color } from 'Client/components/GlobalStyles/theme';

export enum TitlePosition {
    LEFT = 'left',
    CENTER = 'center',
}

interface Props {
    title?: string;
    closeBtnPosition?: Direction.Right | Direction.Left;
    titlePosition: TitlePosition.CENTER | TitlePosition.LEFT;
    showBorder?: boolean;
    dense?: boolean;
    className?: string;
    onCloseClick?: () => void;
    closeIconColor?: Color;
}

export const StyledHeaderContainer = styled.div<{ showBorder?: boolean; dense?: boolean }>`
    padding: ${({ dense }) => (dense ? '' : '20px')} 15px;
    width: 100%;
    color: ${({ theme }) => theme.colors.charcoal100};

    display: grid;
    grid-template-columns: 17px 1fr 17px;
    align-items: center;
    flex: 0;

    border-bottom: ${({ showBorder, theme }) => (showBorder ? `1px solid ${theme.colors.charcoal20}` : 'none')};

    ${forIE`
        display: -ms-grid;
        -ms-grid-columns: 17px 1fr 17px;
    `}
`;

export const StyledTitleContainer = styled.div<{ position: TitlePosition.CENTER | TitlePosition.LEFT }>`
    margin: auto;
    text-align: ${({ position }) => position};
    min-height: 26px;
    grid-row: 1 / span 1;
    grid-column: ${({ position }) => (position === TitlePosition.LEFT ? 1 : 2)} / span 1;
    white-space: nowrap;
    ${header6}
    color: ${({ theme }) => theme.colors.charcoal100};

    ${({ position }) => css`
        ${forIE`
              -ms-grid-row: 1;
              -ms-grid-row-span: 1;
              -ms-grid-column: ${position === TitlePosition.LEFT ? 1 : 2};
              -ms-grid-column-span: 1;
              margin: 28px 0;
        `}
    `}
`;

export const BaseButton = styled.button`
    width: 14px;
    height: 14px;
    grid-row: 1 / span 1;
    cursor: pointer;

    ${forIE`
      -ms-grid-row: 1;
      -ms-grid-row-span: 1;
    `}
`;

export const RightButton = styled(BaseButton)`
    width: 100%;
    height: 100%;
    grid-column: 3 / span 1;

    ${forIE`
      -ms-grid-column: 3;
      -ms-grid-column-span: 1;
    `}
`;

export const LeftButton = styled(BaseButton)`
    grid-column: 1 / span 1;

    ${forIE`
      -ms-grid-column: 1;
      -ms-grid-column-span: 1;
    `}
`;

const Header = ({
    title,
    closeBtnPosition,
    titlePosition,
    showBorder = false,
    dense = false,
    onCloseClick,
    className,
    closeIconColor,
}: Props) => {
    const CloseButtonComponent = closeBtnPosition === Direction.Right ? RightButton : LeftButton;

    return (
        <StyledHeaderContainer showBorder={showBorder} dense={dense} className={className}>
            <CloseButtonComponent type="button" data-testid="flyout-dismiss-click" onClick={onCloseClick}>
                <CrossIcon width={17} height={17} strokeWidth={5} color={closeIconColor} />
            </CloseButtonComponent>
            <StyledTitleContainer position={titlePosition}>{title}</StyledTitleContainer>
        </StyledHeaderContainer>
    );
};

export default Header;
